import { Component, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { SkeletonModule } from 'primeng/skeleton';
import { RmDashboardDealCard } from '../../data-access/relationship-manager-dashboard.model';
import { assetClasses, opportunityStatus, OPPORTUNITY_STATUS_SEVERITY } from '@/pages/deals/data-access/deals.option';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { TagComponent } from '@/shared/ui-components/tag/tag.component';
import { ProgressBarComponent } from '@/shared/ui-components/progress-bar/progress-bar.component';
import { CurrencyDisplayComponent } from '@/shared/ui-components/currency-display/currency-display.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { CustomDatePipe } from '@/shared/pipes/custom-date.pipe';

@Component({
    selector: 'app-rm-deal-cards',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        SvgIconComponent,
        SkeletonModule,
        CardComponent,
        TagComponent,
        ProgressBarComponent,
        CurrencyDisplayComponent,
        ButtonComponent,
        CustomDatePipe,
    ],
    templateUrl: './rm-deal-cards.component.html',
})
export class RmDealCardsComponent {
    private readonly _router = inject(Router);
    private readonly _translate = inject(TranslateService);

    public readonly titleKey = input.required<string>();
    public readonly emptyKey = input.required<string>();
    public readonly viewAllLink = input<string>('/rm/deal-management/deals');
    public readonly items = input<RmDashboardDealCard[]>([]);
    public readonly totalCount = input(0);
    public readonly loading = input(false);

    public showViewAll(): boolean {
        return this.totalCount() > 0;
    }

    public onViewAll(): void {
        void this._router.navigateByUrl(this.viewAllLink());
    }

    public getDealDetailsLink(item: RmDashboardDealCard): unknown[] {
        return ['/rm', 'deal-details', item.id];
    }

    public getAssetClassIcon(assetClass: number | null | undefined): string {
        const icons: Record<number, string> = {
            0: '/icons/private_equity.svg',
            1: '/icons/private_credit.svg',
            2: '/icons/digital_assets.svg',
            3: '/icons/real_estate.svg',
            4: '/icons/commodities.svg',
            5: '/icons/collectibles.svg',
        };
        return icons[assetClass ?? 0] ?? '/icons/private_credit.svg';
    }

    public getAssetClassLabel(assetClass: number | null | undefined): string {
        if (assetClass == null) return '';
        const option = assetClasses.find((item) => item.value === assetClass);
        return option ? (this._translate.instant(option.labelKey) as string) : '';
    }

    public getStatusLabel(status: number | null | undefined): string {
        if (status == null) return '';
        const option = opportunityStatus.find((item) => item.value === status);
        return option ? (this._translate.instant(option.labelKey) as string) : '';
    }

    public getStatusSeverity(
        status: number | null | undefined,
    ): 'danger' | 'warn' | 'success' | 'primary' | 'info' | 'secondary' {
        if (status == null) return 'primary';
        return (
            (OPPORTUNITY_STATUS_SEVERITY[status] as
                | 'danger'
                | 'warn'
                | 'success'
                | 'primary'
                | 'info'
                | 'secondary') ?? 'success'
        );
    }

    public daysRemainingLabel(item: RmDashboardDealCard): string {
        const n = item.daysRemaining;
        if (n == null) return '-';
        if (n <= 0) return 'Closing today';
        if (n === 1) return '1 day remaining';
        return `${n} days remaining`;
    }

    public daysRemainingSeverity(item: RmDashboardDealCard): 'danger' | 'warn' | 'success' | 'primary' {
        const n = item.daysRemaining;
        if (n == null) return 'primary';
        if (n <= 3) return 'danger';
        if (n <= 7) return 'warn';
        return 'success';
    }
}
